import { useEffect } from 'react';
import urlTemplate from 'url-template';
import type { Uuid } from 'src/apis/api-utilities';
import type { BiddingActivityDetails } from 'src/apis/transport-auction/types';
import type { OptionalTransportAuctionServiceRootRequest } from 'src/apis/transport-auction/use-transport-auction-service-root';
import { useAxios, type Successful } from 'src/hooks/use-axios';
import { Forbidden } from 'src/utils/errors';

type HookOptions = {
  root: Successful<OptionalTransportAuctionServiceRootRequest>;
  auctionId: Uuid;
  biddingActivityId: Uuid;
};

type ExecuteOptions = {
  url: string;
};

export function useBiddingActivity({
  root,
  auctionId,
  biddingActivityId,
}: HookOptions) {
  const biddingActivity = useAxios(
    (axios, baseConfig, { url }: ExecuteOptions) =>
      axios.request<BiddingActivityDetails>({
        ...baseConfig,
        url,
      }),
    { neededOnPageLoad: true }
  );

  const biddingActivityLink = root.response.data._links.biddingActivity;

  useEffect(() => {
    if (!biddingActivityLink) return;

    const url = urlTemplate
      .parse(biddingActivityLink.href)
      .expand({ auctionId, biddingActivityId });
    biddingActivity.execute({ url });
    // oxlint-disable-next-line exhaustive-deps
  }, [biddingActivityLink, auctionId, biddingActivityId]);

  if (!biddingActivityLink) throw new Forbidden();

  return biddingActivity;
}

export type BiddingActivityRequest = ReturnType<typeof useBiddingActivity>;
